import React, { useState, useRef, useEffect } from 'react';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { ReactComponent as SearchIcon } from '../icons/search-icon.svg';
import { ReactComponent as LocationPinIcon } from '../icons/location-pin-icon.svg';
import './HeaderSearchBar.css';

const HeaderSearchBar = ({
  searchData = null,
  setSearchData = null,
  handleSearchSubmit = null,
  handleLocationSelect = null,
  showLocationDropdown = false,
  setShowLocationDropdown = null,
  defaultSearchOptions = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchBarRef = useRef(null);
  const dropdownRef = useRef(null);

  useCloseOnEscape(showLocationDropdown, () => setShowLocationDropdown?.(false));

  // Click away listener - mobile-first approach
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both search bar and dropdown
      const isOutsideSearchBar = searchBarRef.current && !searchBarRef.current.contains(event.target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target);
      
      if (isOutsideSearchBar && isOutsideDropdown) {
        if (setShowLocationDropdown && showLocationDropdown) {
          setShowLocationDropdown(false);
        }
      }
    };

    // Use both mousedown and touchstart for better mobile support
    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [setShowLocationDropdown, showLocationDropdown]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    if (setSearchData) {
      setSearchData(prev => ({ ...prev, location: value }));
    } else {
      setSearchQuery(value);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (setShowLocationDropdown && defaultSearchOptions.length > 0) {
      setShowLocationDropdown(true);
    }
  };

  // Handle location selection
  const handleLocationClick = (location) => {
    if (handleLocationSelect) {
      handleLocationSelect(location);
    }
    if (setShowLocationDropdown) {
      setShowLocationDropdown(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (handleSearchSubmit) {
      handleSearchSubmit(e);
    }
    if (setShowLocationDropdown) {
      setShowLocationDropdown(false);
    }
  };

  const inputValue = searchData?.location || searchQuery;
  const shouldShowDropdown = showLocationDropdown && defaultSearchOptions.length > 0;

  return (
    <div className="header-search-container" ref={searchBarRef}>
      <form onSubmit={handleSubmit} className="header-search-form">
        <div
          className="header-search-bar"
          role="combobox"
          aria-expanded={shouldShowDropdown}
          aria-haspopup="listbox"
          aria-controls="header-search-location-listbox"
        >
          <SearchIcon className="header-search-icon" />
          <input
            type="text"
            placeholder="Search venues, locations, or event types..."
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            className="header-search-input"
            aria-label="Search venues, locations, or event types"
            aria-autocomplete="list"
          />
          {shouldShowDropdown && (
            <div 
              className="header-search-location-dropdown" 
              ref={dropdownRef}
              id="header-search-location-listbox"
              role="listbox"
            >
              <div
                className="dropdown-item current-location"
                onClick={() => handleLocationClick('Current location')}
                role="option"
                aria-selected={false}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLocationClick('Current location');
                  }
                }}
              >
                <LocationPinIcon />
                <span>Current location</span>
              </div>
              {defaultSearchOptions.map((option, index) => (
                <div
                  key={index}
                  className="dropdown-item"
                  onClick={() => handleLocationClick(option)}
                  role="option"
                  aria-selected={inputValue === option}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLocationClick(option);
                    }
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default HeaderSearchBar;

